// Торговые идеи — publicare, votare, rating.
//
// Miezul sistemului lor de reputație: soarta unei idei o decid oamenii, nu automatica.
// La patru voturi de o parte ideea se închide, iar autorul primește +2 dacă a mers și
// −1 dacă n-a mers. De aceea nu poți vota propria idee — ratingul s-ar da singur.

import { el, clear, bus, fmtPrice } from '../core/util.js';
import { iconMoneda } from '../ui/coin-icon.js';
import { icon } from '../ui/icons.js';
import { pictAvatar } from '../ui/avatars.js';
import { session, initials } from '../core/session.js';
import { openAuth } from '../ui/auth-modal.js';
import { guestGate } from '../ui/guest-gate.js';
import { modal, toast } from '../ui/components.js';
import { market } from '../core/state.js';

const TABS = [
  { id: '', label: 'Все' },
  { id: 'open', label: 'В работе' },
  { id: 'worked', label: 'Отработали' },
  { id: 'failed', label: 'Не отработали' },
];

const STATUS_META = {
  open: { label: 'В работе', cls: 'open' },
  worked: { label: 'Отработала', cls: 'worked' },
  failed: { label: 'Не отработала', cls: 'failed' },
};

function când(ts) {
  const s = Math.floor(Date.now() / 1000) - ts;
  if (s < 60) return 'только что';
  if (s < 3600) return `${Math.floor(s / 60)} мин назад`;
  if (s < 86400) return `${Math.floor(s / 3600)} ч назад`;
  return new Date(ts * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

/** Procentul unei ținte față de intrare, cu semnul dat de direcție. */
function pct(entry, price, direction) {
  if (!entry || !price) return 0;
  const raw = ((price - entry) / entry) * 100;
  return direction === 'short' ? -raw : raw;
}

export class IdeasSection {
  constructor(app) {
    this.app = app;
    this.tab = '';
    this.items = [];
  }

  async mount(host) {
    this.root = el('div', { class: 'ideas-section' });

    this.tabsEl = el('div', { class: 'ideas-tabs' }, TABS.map((t) => el('button', {
      class: `ideas-tab${t.id === this.tab ? ' active' : ''}`,
      text: t.label,
      onclick: () => { this.tab = t.id; this.#syncTabs(); this.#load(); },
      dataset: { id: t.id },
    })));

    this.listEl = el('div', { class: 'ideas-list' });

    this.root.append(
      el('div', { class: 'ideas-head' }, [
        el('h2', { class: 'ideas-title', text: 'Торговые идеи' }),
        this.tabsEl,
        el('button', {
          class: 'btn primary ideas-new',
          onclick: () => this.#openForm(),
        }, [icon('plus', 12), el('span', { text: 'Опубликовать' })]),
      ]),
      this.listEl,
    );
    host.append(this.root);

    this.offSession = bus.on('session:change', () => this.#load());
    await this.#load();

    // Venit din screener prin «Поделиться» → «В торговую идею»: formularul se deschide
    // precompletat, cu captura graficului deja atașată.
    if (this.app.pendingIdea) {
      const preset = this.app.pendingIdea;
      this.app.pendingIdea = null;
      this.#openForm(preset);
    }
  }

  #syncTabs() {
    for (const b of this.tabsEl.children) b.classList.toggle('active', b.dataset.id === this.tab);
  }

  async #load() {
    try {
      const q = this.tab ? `?status=${this.tab}` : '';
      const data = await (await fetch(`/api/ideas${q}`)).json();
      this.items = data.items || [];
    } catch {
      this.items = [];
    }
    this.#render();
  }

  #render() {
    clear(this.listEl);
    if (!this.items.length) {
      this.listEl.append(el('div', { class: 'ideas-empty' }, [
        el('div', { text: 'Идей пока нет.' }),
        el('div', { class: 'ideas-empty-sub', text: 'Опубликуйте первую — её оценят другие участники.' }),
      ]));
      return;
    }
    for (const idea of this.items) this.listEl.append(this.#card(idea));
  }

  #card(idea) {
    const meta = STATUS_META[idea.status] || STATUS_META.open;
    const dir = idea.direction === 'long';

    const preturi = el('div', { class: 'idea-prices' }, [
      el('div', { class: 'idea-price' }, [
        el('span', { class: 'idea-price-k', text: 'Вход' }),
        el('span', { class: 'idea-price-v', text: fmtPrice(idea.entry) }),
      ]),
      ...idea.targets.map((t, i) => el('div', { class: 'idea-price target' }, [
        el('span', { class: 'idea-price-k', text: `Цель ${i + 1}` }),
        el('span', { class: 'idea-price-v', text: fmtPrice(t) }),
        el('span', { class: 'idea-price-p up', text: `+${pct(idea.entry, t, idea.direction).toFixed(2)}%` }),
      ])),
      ...idea.stops.map((s) => el('div', { class: 'idea-price stop' }, [
        el('span', { class: 'idea-price-k', text: 'Стоп' }),
        el('span', { class: 'idea-price-v', text: fmtPrice(s) }),
        el('span', { class: 'idea-price-p down', text: `${pct(idea.entry, s, idea.direction).toFixed(2)}%` }),
      ])),
    ]);

    return el('article', { class: `idea-card ${meta.cls}` }, [
      el('div', { class: 'idea-head' }, [
        (() => {
          const a = el('span', { class: 'chat-avatar',
            style: { background: idea.author.status.color } });
          pictAvatar(a, idea.author, 34);
          return a;
        })(),
        el('div', { class: 'idea-who' }, [
          el('button', {
            class: 'idea-author', text: idea.author.username,
            style: { color: idea.author.status.color },
            onclick: () => bus.emit('profile:open', { username: idea.author.username }),
          }),
          el('span', { class: 'idea-when', text: când(idea.created_at) }),
        ]),
        el('span', { class: `idea-status ${meta.cls}`, text: meta.label }),
      ]),

      el('div', { class: 'idea-main' }, [
        el('button', {
          class: 'idea-symbol',
          title: 'Открыть в скринере',
          onclick: () => bus.emit('symbol:open', { symbol: idea.symbol }),
        }, [
          iconMoneda(idea.symbol, 16),
          el('span', { text: idea.symbol.replace(/USDT$/, '') }),
          icon('chartLine', 11),
        ]),
        el('span', { class: `idea-dir ${dir ? 'long' : 'short'}`, text: dir ? 'LONG' : 'SHORT' }),
        idea.setup ? el('span', { class: 'idea-setup', text: idea.setup }) : null,
      ]),

      preturi,
      this.#track(idea),
      idea.shot
        ? el('img', {
          class: 'idea-shot', src: idea.shot, alt: idea.symbol,
          onclick: () => window.open(idea.shot, '_blank'),
        })
        : null,
      idea.text ? el('p', { class: 'idea-text', text: idea.text }) : null,
      this.#votes(idea),
    ]);
  }

  /**
   * Ce a făcut prețul de la publicare — faptele, măsurate de server.
   *
   * Stă separat de voturi intenționat: prețul poate atinge ținta fără ca ideea să fi
   * fost bună, iar oamenii votează ideea, nu prețul.
   */
  #track(idea) {
    const t = idea.track;
    if (!t || !t.checked_at) return null;
    const total = idea.targets.length;

    return el('div', { class: 'idea-track' }, [
      total
        ? el('span', {
          class: `idea-track-chip${t.targets_hit ? ' hit' : ''}`,
          text: `Цели: ${t.targets_hit} из ${total}`,
        })
        : null,
      t.stop_hit
        ? el('span', { class: 'idea-track-chip stop', text: 'Стоп задет' })
        : null,
      el('span', { class: 'idea-track-run' }, [
        el('span', { class: 'up', text: `макс +${t.best_pct.toFixed(2)}%` }),
        el('span', { class: 'down', text: `мин ${t.worst_pct.toFixed(2)}%` }),
      ]),
    ]);
  }

  #votes(idea) {
    const buton = (vot, eticheta, cls) => {
      const activ = idea.votes.mine === vot;
      const n = vot > 0 ? idea.votes.worked : idea.votes.failed;
      return el('button', {
        class: `idea-vote ${cls}${activ ? ' mine' : ''}`,
        disabled: !idea.can_vote && !activ,
        title: idea.can_vote ? '' : 'Голосовать может только другой участник, пока идея открыта',
        onclick: () => this.#vote(idea, vot),
      }, [
        el('span', { text: eticheta }),
        el('span', { class: 'idea-vote-n', text: String(n) }),
      ]);
    };

    return el('div', { class: 'idea-votes' }, [
      buton(1, 'Отработала', 'up'),
      buton(-1, 'Не отработала', 'down'),
      idea.status === 'open'
        ? el('span', { class: 'idea-vote-hint', text: 'Решают участники: 4 голоса закрывают идею' })
        : el('span', {
          class: `idea-vote-hint ${idea.status}`,
          text: idea.status === 'worked' ? 'Автор получил +2 к рейтингу' : 'Автор получил −1 к рейтингу',
        }),
    ]);
  }

  async #vote(idea, vot) {
    if (!session.authenticated) { openAuth(); return; }
    try {
      const resp = await fetch(`/api/ideas/${idea.id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vote: vot }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail);
      // Înlocuim doar cardul votat, ca lista să nu sară sub degete.
      const i = this.items.findIndex((x) => x.id === idea.id);
      if (i >= 0) {
        const inainte = this.items[i].status;
        this.items[i] = data.idea;
        this.listEl.children[i]?.replaceWith(this.#card(data.idea));
        if (inainte === 'open' && data.idea.status !== 'open') {
          toast(data.idea.status === 'worked'
            ? 'Идея закрыта: отработала'
            : 'Идея закрыта: не отработала', 'info');
        }
      }
    } catch (err) {
      toast(err.message || 'Не удалось проголосовать', 'error');
    }
  }

  // ------------------------------------------------------------- formular

  async #openForm(preset = {}) {
    if (!session.authenticated) { openAuth(); return; }

    let setups = [];
    try {
      setups = (await (await fetch('/api/community/setups')).json()).items || [];
    } catch { /* fără șabloane, câmpul rămâne liber */ }

    let directie = 'long';
    const shot = preset.shot || '';
    const symbolEl = el('input', {
      class: 'auth-input', placeholder: 'BTCUSDT', list: 'idea-symbols',
      value: preset.symbol || '',
    });
    const entryEl = el('input', { class: 'auth-input', type: 'number', step: 'any', placeholder: 'Цена входа' });

    // «С текущих», ca la ei: prețul de acum, fără să-l cauți pe grafic.
    const acumEl = el('button', {
      class: 'idea-now', type: 'button',
      title: 'Подставить текущую цену',
      onclick: () => {
        const p = preset.price || market.get(symbolEl.value.trim().toUpperCase())?.price;
        if (!p) { toast('Цена ещё не загружена', 'error'); return; }
        entryEl.value = p;
        entryEl.dispatchEvent(new Event('input'));
      },
    }, [el('span', { text: 'С текущих' })]);
    const textEl = el('textarea', { class: 'idea-form-text', rows: 3, placeholder: 'Логика идеи, разметка, что подтверждает вход…' });

    // Sugestii de monede din lista deja încărcată — fără o cerere în plus.
    const datalist = el('datalist', { id: 'idea-symbols' },
      [...(market.rows?.keys() || [])].slice(0, 300).map((s) => el('option', { value: s })));

    const dirBtns = ['long', 'short'].map((d) => el('button', {
      class: `idea-dirbtn ${d}${d === directie ? ' active' : ''}`,
      text: d.toUpperCase(),
      onclick: (e) => {
        directie = d;
        for (const b of e.target.parentElement.children) b.classList.toggle('active', b.textContent === d.toUpperCase());
        recalc();
      },
    }));

    const setupEl = el('div', { class: 'idea-setups' }, setups.map((s) => el('button', {
      class: 'idea-setup-btn', text: s,
      onclick: (e) => {
        for (const b of e.target.parentElement.children) b.classList.remove('active');
        e.target.classList.add('active');
      },
    })));

    // Ținte și stopuri: prețul și procentul se recalculează unul din celălalt, ca la ei.
    const linii = [];
    const makeLine = (fel) => {
      const price = el('input', { class: 'auth-input mini', type: 'number', step: 'any', placeholder: 'Цена' });
      const percent = el('input', { class: 'auth-input mini', type: 'number', step: 'any', placeholder: '%' });
      price.oninput = () => {
        const e = Number(entryEl.value);
        if (e > 0 && price.value) percent.value = pct(e, Number(price.value), directie).toFixed(2);
      };
      percent.oninput = () => {
        const e = Number(entryEl.value);
        if (!e || percent.value === '') return;
        const p = Number(percent.value) / 100;
        price.value = (directie === 'short' ? e * (1 - p) : e * (1 + p)).toPrecision(8).replace(/0+$/, '');
      };
      const row = el('div', { class: `idea-line ${fel}` }, [
        el('span', { class: 'idea-line-k', text: fel === 'target' ? 'Цель' : 'Стоп' }),
        price, percent,
      ]);
      linii.push({ fel, price, percent });
      return row;
    };

    const recalc = () => {
      for (const l of linii) if (l.price.value) l.price.oninput();
    };
    entryEl.oninput = recalc;

    const targetsBox = el('div', { class: 'idea-lines' }, [makeLine('target'), makeLine('target')]);
    const stopsBox = el('div', { class: 'idea-lines' }, [makeLine('stop')]);

    const errEl = el('div', { class: 'auth-error' });

    const body = el('div', { class: 'idea-form' }, [
      datalist,
      el('div', { class: 'idea-form-row' }, [
        el('label', { class: 'auth-field grow' }, [icon('search', 12), symbolEl]),
        el('div', { class: 'idea-dirs' }, dirBtns),
      ]),
      el('div', { class: 'idea-form-row' }, [
        el('label', { class: 'auth-field grow' }, [icon('coins', 12), entryEl]),
        acumEl,
      ]),
      // Captura vine cu desenele de pe grafic — de asta e atașată automat, nu cerută.
      shot ? el('div', { class: 'idea-form-shot' }, [
        el('img', { class: 'share-preview', src: shot, alt: '' }),
        el('span', { class: 'idea-form-shotnote', text: 'Скриншот графика с вашей разметкой прикреплён' }),
      ]) : null,
      el('div', { class: 'idea-form-label', text: 'Сетап' }), setupEl,
      el('div', { class: 'idea-form-label', text: 'Цели' }), targetsBox,
      el('div', { class: 'idea-form-label', text: 'Стоп' }), stopsBox,
      textEl,
      errEl,
    ]);

    const dlg = modal({
      title: 'Новая торговая идея',
      width: 520,
      body,
      actions: [
        { label: 'Отмена', onClick: (c) => c() },
        {
          label: 'Опубликовать', primary: true,
          onClick: async (c) => {
            errEl.textContent = '';
            const payload = {
              symbol: symbolEl.value, direction: directie,
              exchange: preset.exchange || this.app.exchange,
              market: preset.market || this.app.market,
              tf: preset.tf || '15m',
              shot,
              entry: entryEl.value,
              setup: setupEl.querySelector('.active')?.textContent || '',
              targets: linii.filter((l) => l.fel === 'target').map((l) => l.price.value),
              stops: linii.filter((l) => l.fel === 'stop').map((l) => l.price.value),
              text: textEl.value,
            };
            try {
              const resp = await fetch('/api/ideas', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              });
              const data = await resp.json();
              if (!resp.ok) throw new Error(data.detail);
              c();
              toast('Идея опубликована', 'success');
              this.tab = '';
              this.#syncTabs();
              this.#load();
            } catch (err) {
              errEl.textContent = err.message || 'Не удалось опубликовать';
            }
          },
        },
      ],
    });
    setTimeout(() => symbolEl.focus(), 30);
    return dlg;
  }

  unmount() {
    this.offSession?.();
    this.root?.remove();
  }
}
